import React from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';

// components
import ShippingIcon from 'dibs-vg/dist/react/shipping';
import PdpSharedBlurb from './PdpSharedBlurb/PdpSharedBlurb';
import PdpSharedShippingModalLoader from '../PdpSharedModals/PdpSharedShippingModal/PdpSharedShippingModalLoader';
import PdpSharedBlurbShippingCustomQuote from './PdpSharedBlurbShippingCustomQuote';
import { shippingTitle } from '../messages/pdpBlurbMessages';
import { viewDetails } from '../pdpMessages';
import { viewQuotes, requestQuotes, viewAndRequestQuotes } from '../messages/pdpShippingMessages';

// styles
import styles from './PdpSharedBlurbs.scss';

// helpers
import tracking from 'dibs-tracking';
import { buildShippingDetails } from '../helpers/shippingHelpers';
import { getQueryParam } from '../../utils/uriUtils';
import get from 'lodash.get';

export class PdpSharedBlurbShipping extends React.Component {
    constructor(props) {
        super(props);

        this.state = { modalOpen: false };
        this.openShippingModal = this.openShippingModal.bind(this);
        this.closeShippingModal = this.closeShippingModal.bind(this);
    }

    componentDidMount() {
        const modalParam = getQueryParam(get(global, 'location.search'), 'modal');
        if (modalParam === 'shipping') {
            this.openShippingModal();
        }
    }

    openShippingModal(e) {
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
        }
        this.setState({ modalOpen: true }, () => {
            const { hasQuote } = this.getShippingDetails();
            const canRequestShippingQuote = this.getCanRequestShippingQuote();

            const trackingLabels = ['pdp sidebar'];
            trackingLabels.push(`${hasQuote ? 'shipping' : 'no shipping'} quote provided`);
            if (canRequestShippingQuote) {
                trackingLabels.push('custom quote request available');
            }

            tracking.trackEvent({
                category: 'product',
                action: 'view shipping details',
                label: trackingLabels.join('|'),
            });

            // MCOPOP-5428: tracking for users with request shipping permission
            if (canRequestShippingQuote && !hasQuote) {
                tracking.trackEvent({
                    category: 'contact dealer',
                    action: 'contact dealer clicked',
                    label: 'request a shipping quote - pdp sidebar',
                });
            }
        });
    }

    closeShippingModal() {
        this.setState({ modalOpen: false });
    }

    getShippingDetails() {
        const { currency, localDeliveryOptions, domesticDeliveryOptions, viewer } = this.props;
        return buildShippingDetails({
            currency,
            localDeliveryOptions,
            domesticDeliveryOptions,
            shipmentQuotes: get(viewer, 'shipmentQuotes'),
            relayContext: this.context.relay,
        });
    }

    /**
     * only trade users can request a shipping quote
     * (unless enabled for all logged in users)
     */
    getCanRequestShippingQuote() {
        const { viewer, user } = this.props;
        // return false for all logged out users
        if (!user) {
            return false;
        }
        // if enabled return true for logged in users
        if (viewer.requestForAllEnabled) {
            return true;
        }
        // only members of the trade can request
        return !!user.isVerifiedTrade;
    }

    hasValidCustomQuote() {
        const quotes = get(this.props, 'item.lastTransaction.shipmentQuotes');
        const customQuotes = (quotes || []).filter(
            quote => quote.isCustom && quote.status !== 'NEW'
        );

        return !!customQuotes.length;
    }

    render() {
        const { currency, userId, itemId, item, relay } = this.props;
        const lastTransaction = get(item, 'lastTransaction');
        const { output, hasQuote } = this.getShippingDetails();
        const canRequestShippingQuote = this.getCanRequestShippingQuote();
        const hasValidCustomQuote = this.hasValidCustomQuote();

        if (!output && !hasValidCustomQuote) {
            return null;
        }

        // link cta
        let linkCta = viewDetails;
        if ((hasQuote || hasValidCustomQuote) && canRequestShippingQuote) {
            linkCta = viewAndRequestQuotes;
        } else if (hasQuote) {
            linkCta = viewQuotes;
        } else if (canRequestShippingQuote) {
            linkCta = requestQuotes;
        }

        return (
            <PdpSharedBlurb Icon={ShippingIcon}>
                <span className={styles.title}>{shippingTitle}: </span>
                {!hasValidCustomQuote && output}
                {hasValidCustomQuote && (
                    <PdpSharedBlurbShippingCustomQuote
                        transaction={lastTransaction}
                        currency={currency}
                    />
                )}
                <div>
                    <a href="#shipping-modal" onClick={this.openShippingModal}>
                        {linkCta}
                    </a>
                    <PdpSharedShippingModalLoader
                        currency={currency}
                        userId={userId}
                        itemId={itemId}
                        environment={relay.environment}
                        canRequestShippingQuote={canRequestShippingQuote}
                        hasQuote={hasQuote}
                        isOpened={this.state.modalOpen}
                        closeShippingModal={this.closeShippingModal}
                        trackingLocation="pdp sidebar"
                    />
                </div>
            </PdpSharedBlurb>
        );
    }
}

PdpSharedBlurbShipping.propTypes = {
    currency: PropTypes.string,
    userId: PropTypes.string,
    itemId: PropTypes.string,
    viewer: PropTypes.object.isRequired,
    user: PropTypes.object,
    item: PropTypes.object.isRequired,
    localDeliveryOptions: PropTypes.object,
    domesticDeliveryOptions: PropTypes.object,
    relay: PropTypes.object.isRequired,
};

PdpSharedBlurbShipping.contextTypes = {
    relay: PropTypes.object.isRequired,
};

export default createFragmentContainer(PdpSharedBlurbShipping, {
    viewer: graphql`
        fragment PdpSharedBlurbShipping_viewer on Viewer {
            requestForAllEnabled: isEnabled(key: "enableRequestQuoteForAllLoggedIn")
            shipmentQuotes(itemId: $itemId, searchView: PDP, first: 10) {
                ...shippingHelpers_shipmentQuotesConnection
            }
        }
    `,
    user: graphql`
        fragment PdpSharedBlurbShipping_user on User {
            isVerifiedTrade
        }
    `,
    item: graphql`
        fragment PdpSharedBlurbShipping_item on Item {
            lastTransaction(buyerId: $userId) @include(if: $fetchUser) {
                shipmentQuotes {
                    isCustom
                }
                ...PdpSharedBlurbShippingCustomQuote_transaction
            }
        }
    `,
    localDeliveryOptions: graphql`
        fragment PdpSharedBlurbShipping_localDeliveryOptions on ShipmentQuoteConnection {
            ...shippingHelpers_shipmentQuoteConnection
        }
    `,
    domesticDeliveryOptions: graphql`
        fragment PdpSharedBlurbShipping_domesticDeliveryOptions on ShipmentQuoteConnection {
            ...shippingHelpers_shipmentQuoteConnection
        }
    `,
});
